'use client';

import { ChevronIcon, ExternalLink, InternalLink } from '@/components/ui';
import { Image } from '@/components/ui/Image';
import { CONTENT_TYPE_LINK, getContentType } from '@/core/constants';
import interpolate from '@/utils/translations/interpolate';
import { UniversalLink, UniversalLinkFields } from '../UniversalLink';
import { Overridable } from '../Overridable';
import { AuthState } from '@/context/AuthContext';
import { buildMenu } from './NavMenu';
import useUserCourses from '@/lib/api/playbackService/hooks/useUserCourses';
import { shouldHideWithLocaleOverride } from '@/utils/localeOverride';
import { Entry } from 'contentful';
import { FC, useMemo, useState } from 'react';
import { VariationMenuItem } from './VariationNavMenuItem';
import { slugBuilder } from '@/core/contentfulConfig';

import {
  CLICKED_ITEM_TYPES,
  trackItemClickedEvent,
  PAGE_LOCATIONS,
} from '@/utils/rudderstack';

interface SubMenuItemProps {
  menuItem: Entry<UniversalLinkFields>;
}

const SubMenuItem: FC<SubMenuItemProps> = ({ menuItem }) => {
  return (
    <li className="mb-2 last:mb-0" data-testid={menuItem.fields.entryName}>
      <UniversalLink link={menuItem} classes="mr-6 xl:mr-9 hover:text-indigo" />
    </li>
  );
};

const MaybeRenderCoursePacksLink: FC<SubMenuItemProps> = ({ menuItem }) => {
  const shouldFetch = !shouldHideWithLocaleOverride(
    menuItem.fields.localeOverride,
  );
  const { data, loading } = useUserCourses(shouldFetch);

  if (loading) return null;

  const userHasCourseAccess = data?.courses?.length;

  if (!userHasCourseAccess) return null;

  return <SubMenuItem menuItem={menuItem} />;
};

const sendItemClickedEvent = (menuItem: any) => {
  const { linkText } = menuItem.fields;

  trackItemClickedEvent({
    url: slugBuilder(menuItem),
    type: CLICKED_ITEM_TYPES.LINK,
    pageLocation: PAGE_LOCATIONS.NAVIGATION,
    cta: linkText,
  });
};

const buildSubMenuItem = (menuItem: any) => {
  switch (getContentType(menuItem)) {
    case CONTENT_TYPE_LINK.INTERNAL:
      return (
        <Overridable content={menuItem}>
          <InternalLink
            key={`${menuItem.sys.id}-${menuItem.fields.linkText}`}
            link={menuItem}
            onClick={() => sendItemClickedEvent(menuItem)}
          >
            <div className="mx-4 text-center">
              <figure className="text-center">
                {menuItem.fields.linkImage && (
                  <Image
                    imageData={menuItem.fields.linkImage}
                    className="h-44 object-cover"
                    height={176}
                    width={192}
                    alt=""
                  />
                )}
              </figure>
              <div className="pt-3 text-base font-bold">
                {menuItem.fields.linkText}
              </div>
            </div>
          </InternalLink>
        </Overridable>
      );

    case CONTENT_TYPE_LINK.EXTERNAL:
      return (
        <Overridable content={menuItem}>
          <ExternalLink
            key={`${menuItem.sys.id}-${menuItem.fields.linkText}`}
            link={menuItem}
            onClick={() => sendItemClickedEvent(menuItem)}
          >
            <div className="mx-4 text-center">
              <figure className="text-center">
                {menuItem.fields.linkImage && (
                  <Image
                    imageData={menuItem.fields.linkImage}
                    className="h-44 object-cover"
                    height={176}
                    width={192}
                    alt=""
                  />
                )}
              </figure>
              <div className="pt-3 text-base font-bold">
                {menuItem.fields.linkText}
              </div>
            </div>
          </ExternalLink>
        </Overridable>
      );

    default:
      console.warn(
        'Unsupported submenu content type',
        getContentType(menuItem),
      );
      return null;
  }
};

export interface NavMenuItemProps {
  menuItem: any;
  i: number;
  user?: AuthState | null;
  logoutText?: string;
  onClickSignOut?: () => void;
}

export const NavMenuItem = (props: NavMenuItemProps) => {
  const { menuItem, i, user, logoutText, onClickSignOut } = props;
  const [isExpanded, setIsExpanded] = useState(false);

  const textClass = useMemo(() => {
    const textColor = props.menuItem.fields.textColor;
    if (!textColor) return 'hover:text-blue-deep';
    return `text-${textColor.fields?.color}`;
  }, [props.menuItem.fields.textColor]);

  switch (getContentType(menuItem)) {
    case 'launchDarklyVariationContainer':
      return VariationMenuItem({ ...props });
    case 'list':
      if (menuItem.fields.entryName === 'User Menu') {
        return (
          <div className="dropdown dropdown-hover" key={i}>
            <label
              tabIndex={0}
              className={`mr-6 flex w-max flex-row items-center font-bold ${textClass} xl:mr-9`}
            >
              {interpolate(menuItem.fields.title, {
                first_name: user?.attributes?.name || 'user',
              })}
              <ChevronIcon direction="down" classes="w-4 h-4 ml-2" />
            </label>
            <ul
              key={menuItem.sys.id}
              tabIndex={0}
              className="dropdown-content menu w-max bg-white px-4 py-6 font-normal shadow-lg"
            >
              {menuItem.fields.listItems &&
                (buildMenu(menuItem.fields.listItems) as any)}
              <li>
                <button
                  className={`flex cursor-pointer flex-row items-center ${textClass}`}
                  onClick={onClickSignOut}
                >
                  {logoutText}
                </button>
              </li>
            </ul>
          </div>
        );
      } else if (menuItem.fields.entryName === "'Shop' Menu Links Desktop") {
        return (
          <div className="dropdown dropdown-hover static" key={i}>
            <label
              tabIndex={0}
              className={`mr-6 flex w-max flex-row items-center font-bold ${textClass} xl:mr-9`}
              onMouseEnter={() => setIsExpanded(true)}
            >
              {interpolate(menuItem.fields.title, {
                first_name: user?.attributes?.name || 'user',
              })}
              <ChevronIcon direction="down" classes="w-4 h-4 ml-2" />
            </label>
            {isExpanded && (
              <div className="dropdown-content menu w-max bg-white font-normal shadow-lg p-6 left-0 !w-full">
                <ul
                  key={menuItem.sys.id}
                  tabIndex={0}
                  className="flex w-full flex-row items-start justify-center"
                >
                  {menuItem.fields.listItems.map(
                    (subMenuItem: {
                      sys: { id: React.Key | null | undefined };
                    }) => {
                      return (
                        <li
                          className="max-w-[224px] hover:opacity-60 hover:transition-opacity hover:duration-500 hover:ease-in-out"
                          key={subMenuItem.sys.id}
                          onClick={() => {
                            (document.activeElement as HTMLElement).blur();
                            setIsExpanded(false);
                          }}
                        >
                          {buildSubMenuItem(subMenuItem as any)}
                        </li>
                      );
                    },
                  )}
                </ul>
              </div>
            )}
          </div>
        );
      } else {
        return (
          <div className="dropdown static" key={i}>
            <label
              tabIndex={0}
              className={`mr-6 flex w-max flex-row items-center font-bold ${textClass} xl:mr-9`}
            >
              {menuItem.fields.title}
              <ChevronIcon direction="down" classes="w-4 h-4 ml-2" />
            </label>
            <ul
              key={menuItem.sys.id}
              tabIndex={0}
              className="dropdown-content menu left-0 top-[80px] flex w-full flex-row items-center justify-center bg-white py-12 shadow-lg"
            >
              {menuItem.fields.listItems.map(
                (subMenuItem: {
                  sys: { id: React.Key | null | undefined };
                }) => {
                  return (
                    <li
                      className="hover:opacity-60 hover:transition-opacity hover:duration-500 hover:ease-in-out"
                      key={subMenuItem.sys.id}
                      onClick={() =>
                        (document.activeElement as HTMLElement).blur()
                      }
                    >
                      {buildSubMenuItem(subMenuItem as any)}
                    </li>
                  );
                },
              )}
            </ul>
          </div>
        );
      }

    default:
      if (menuItem.fields.entryName === 'My Course Packs') {
        return (
          <MaybeRenderCoursePacksLink
            menuItem={menuItem}
            key={menuItem.sys.id}
          />
        );
      }

      return (
        <li
          data-testid={menuItem.fields.entryName}
          key={menuItem.fields.entryName}
        >
          <UniversalLink
            link={menuItem}
            onClick={() => sendItemClickedEvent(menuItem)}
            classes={`mr-6 xl:mr-9 min-w-[60px] ${textClass}`}
          />
        </li>
      );
  }
};
